/**
 * 상세 수수료율 설정 페이지 — 레거시 `detailFeeRateSetting.js` 포팅.
 * 병원/의원 시나리오별 제약사·의약품 수수료율 설정 + dirty 상태 관리.
 */
import { businessService, type BusinessRelationshipResponse } from '@/entities/business/api/business.api';
import { feeRateService, type BasicFeeType } from '@/entities/fee-rate/api/fee-rate.api';
import {
  useDetailFeeGroupClinics,
  useRegisterDetailFeeGroup,
  useSaveClinicDrugRates,
  useSaveClinicPharmaRates,
  useSaveHospitalDrugRules,
} from '@/entities/fee-rate/hooks/useFeeRates';
import { DetailFeeDrugAddModal } from '@/pages/fee-rates/DetailFeeDrugAddModal';
import {
  areRatesEqual,
  buildClinicDrugRatesPayload,
  buildClinicPharmaRatesPayload,
  buildDetailDataByRelationships,
  buildDetailDataSnapshot,
  buildHospitalDrugRulesPayload,
  createCurrentToNextMonthRateRow,
  createNextSequentialRateRow,
  mergeDetailRatesFromServer,
  normalizeDrugRate,
  normalizePharmaRate,
  syncHospitalAllPharmaDrugsToEachPharma,
  syncHospitalSelectedPharmaDrugsToAllPharma,
  validateAppliedPeriods,
  type BusinessRelationshipItem,
  type DetailDataUi,
  type DrugGroupUi,
  type PharmaGroupUi,
  type RateRowUi,
} from '@/pages/fee-rates/detailFeeSettingUtils';
import {
  formatRateInput,
  roundSignedRate,
} from '@/pages/fee-rates/feeRateUtils';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MonthPicker } from '@/shared/components/DatePicker';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Stack, ToolbarRow } from '@/shared/components/ui/row';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft, Plus, Save, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

const EMPTY_DETAIL_DATA: DetailDataUi = {
  pharmaGroups: [],
  selectedPharmaId: undefined,
  drugGroupsByPharmaId: {},
};

interface SearchParams {
  userId?: string;
  email?: string;
  companyName?: string;
  basicFeeType?: string;
}

export const DetailFeeRateSettingPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { readOnly } = useMenuAccess();
  const search = useSearch({ strict: false }) as SearchParams;

  const { userId, email, companyName } = search;
  const basicFeeType = search.basicFeeType as BasicFeeType | undefined;
  
  // 구간 수수료 여부 (basicFeeType이 RANGE 또는 '구간')
  const isIntervalFee = basicFeeType === 'RANGE' || search.basicFeeType === '구간';

  // 상태
  const [selectedClinicId, setSelectedClinicId] = useState<number | null>(null);
  const [detailData, setDetailData] = useState<DetailDataUi>(EMPTY_DETAIL_DATA);
  const [detailGroupId, setDetailGroupId] = useState<number | null>(null);
  const [detailGroupData, setDetailGroupData] = useState<any>(null);
  const [clinicDetailGroup, setClinicDetailGroup] = useState<any>(null);
  const [relationshipRows, setRelationshipRows] = useState<BusinessRelationshipItem[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // 모달 상태
  const [exitOpen, setExitOpen] = useState(false);
  const [clinicChangeBlockedOpen, setClinicChangeBlockedOpen] = useState(false);
  const [drugAddModalOpen, setDrugAddModalOpen] = useState(false);
  const [deleteRowTarget, setDeleteRowTarget] = useState<{
    groupType: 'pharma' | 'drug';
    groupId: string;
    rowId: string;
  } | null>(null);
  const [uncheckFixedTarget, setUncheckFixedTarget] = useState<{
    groupType: 'pharma' | 'drug';
    groupId: string;
  } | null>(null);
  /** 수수료율 입력 포커스 중일 때만 draft 문자열로 편집 (비포커스는 소수점 2자리) */
  const [focusedRateRowId, setFocusedRateRowId] = useState<string | null>(null);
  /** '-', '-0.' 처럼 숫자로 바꾸기 전 중간 입력을 보존하기 위한 문자열 */
  const [focusedRateDraft, setFocusedRateDraft] = useState('');
  
  // 더티 체크용 참조
  const originalDetailDataSnapshotRef = useRef<string | null>(null);

  // 훅들
  const { data: clinics, isLoading: clinicsLoading } = useDetailFeeGroupClinics(
    userId ? Number(userId) : 0
  );

  const registerMutation = useRegisterDetailFeeGroup();
  const saveHospitalMutation = useSaveHospitalDrugRules();
  const saveClinicPharmaMutation = useSaveClinicPharmaRates();
  const saveClinicDrugMutation = useSaveClinicDrugRates();

  // 현재 선택된 클리닉 정보
  const selectedClinic = useMemo(() => {
    if (!selectedClinicId || !clinics) return null;
    return clinics.find((c) => c.clinicId === selectedClinicId) || null;
  }, [selectedClinicId, clinics]);

  const isHospitalScenario = selectedClinic?.clinicType === 'HOSPITAL';

  // 현재 표시할 의약품 그룹들
  const currentDrugGroups = detailData.drugGroupsByPharmaId?.[detailData.selectedPharmaId || ''] || [];
  const sortedCurrentDrugGroups = [...currentDrugGroups].sort((a, b) =>
    String(a?.name || '').localeCompare(String(b?.name || ''), 'ko')
  );

  // 표시할 제약사 그룹들 (병원: 전체 제약사 + 개별, 의원: 승인된 제약사만)
  const visiblePharmaGroups = useMemo(() => {
    const sorted = [...detailData.pharmaGroups].sort((a, b) =>
      String(a?.name || '').localeCompare(String(b?.name || ''), 'ko')
    );
    
    return isHospitalScenario
      ? [{ id: 'all-pharma', name: '전체 제약사', pharmaCompanyId: null, fixedEnabled: false, rows: [] }, ...sorted]
      : sorted;
  }, [detailData.pharmaGroups, isHospitalScenario]);

  // 현재 선택된 제약사 그룹
  const selectedPharmaGroup = detailData.pharmaGroups.find(
    (item) => item.id === detailData.selectedPharmaId
  );

  // 기간 유효성 검사
  const invalidPeriodTarget = useMemo(() => {
    return validateAppliedPeriods(detailData);
  }, [detailData]);

  const hasInvalidPeriod = Boolean(invalidPeriodTarget);

  // ===== 데이터 로딩 =====

  /**
   * 비즈니스 관계 데이터 조회
   */
  const fetchRelationshipRows = async (email: string) => {
    try {
      const response = await businessService.getBusinessRelationships(
        { email, drugApprovalStatus: 'APPROVED', transactionStatus: '승인' },
        { page: 0, size: 1000 }
      );
      // 타입 변환하여 설정 (레거시 매핑 포함)
      const items: BusinessRelationshipItem[] = (response.content || []).map((item: BusinessRelationshipResponse) => ({
        ...item,
        clinicId: item.clinicId || 0,
        clinicName: item.clinicName || '',
        clinicType: (item.clinicType as 'HOSPITAL' | 'CLINIC') || 'CLINIC',
        // pharmaCompanyId는 item.pharmaCompanyId에서 직접 매핑 (relationPharmaCompanyId와 별개 필드)
        pharmaCompanyId: item.pharmaCompanyId || undefined,
        pharmaCompanyName: item.pharmaCompanyName,
        drugId: item.drugId || undefined,
        drugName: item.drugName || undefined,
        drugCode: item.drugCode || undefined,
        drugApprovalStatus: item.drugApprovalStatus || undefined,
        transactionStatus: item.transactionStatus || undefined,
      }));
      setRelationshipRows(items);
    } catch (err) {
      console.error('Failed to fetch relationship rows:', err);
      handleApiError(err, toast);
      setRelationshipRows([]);
    }
  };

  /**
   * 상세 수수료 그룹 조회 및 등록
   */
  const fetchDetailGroup = async (clinicId: number) => {
    if (!userId || !clinicId || !selectedClinic || !basicFeeType) return;

    try {
      const params = {
        userId: Number(userId),
        clinicId,
        basicFeeType,
      };

      // 클리닉 타입에 따라 다른 API 호출
      const detailGroup = isHospitalScenario 
        ? await feeRateService.getHospitalDetailFeeGroup(params)
        : await feeRateService.getClinicDetailFeeGroup(params);

      if (!detailGroup) {
        // 그룹이 없으면 등록 후 재조회
        await registerMutation.mutateAsync({
          userId: Number(userId),
          clinicId,
          clinicType: selectedClinic.clinicType as 'HOSPITAL' | 'CLINIC',
          basicFeeType,
        });
        
        // 재조회
        const createdGroup = isHospitalScenario
          ? await feeRateService.getHospitalDetailFeeGroup(params)
          : await feeRateService.getClinicDetailFeeGroup(params);
        
        setDetailGroupId(createdGroup?.detailGroupId ?? null);
        setDetailGroupData(createdGroup || null);
        setClinicDetailGroup(isHospitalScenario ? null : (createdGroup || null));
        return;
      }

      setDetailGroupId(detailGroup?.detailGroupId ?? null);
      setDetailGroupData(detailGroup || null);
      setClinicDetailGroup(isHospitalScenario ? null : (detailGroup || null));
    } catch (error) {
      console.error('Failed to fetch detail fee group:', error);
      handleApiError(error, toast);
      setDetailGroupId(null);
      setDetailGroupData(null);
      setClinicDetailGroup(null);
    }
  };

  // ===== Effects =====

  // 초기 데이터 로딩
  useEffect(() => {
    if (!email || !userId) return;
    fetchRelationshipRows(email);
  }, [email, userId]);

  // 클리닉 자동 선택
  useEffect(() => {
    if (!clinics || clinics.length === 0) return;
    setSelectedClinicId(clinics[0].clinicId);
  }, [clinics]);

  // 클리닉 변경 시 상세 그룹 조회
  useEffect(() => {
    if (!selectedClinicId) {
      setDetailGroupId(null);
      setDetailGroupData(null);
      setClinicDetailGroup(null);
      originalDetailDataSnapshotRef.current = null;
      setIsDirty(false);
      return;
    }

    fetchDetailGroup(selectedClinicId);
  }, [selectedClinicId, userId, basicFeeType, isHospitalScenario]);

  // 관계 데이터 + 서버 데이터 병합
  useEffect(() => {
    const clinicRows = relationshipRows.filter(
      (item) => String(item.clinicId) === String(selectedClinicId)
    );

    const base = buildDetailDataByRelationships({
      relationships: clinicRows,
      isHospitalScenario,
    });

    const serverData = {
      clinicPharmaRates: detailGroupData?.clinicPharmaRates,
      hospitalDrugRules: detailGroupData?.hospitalDrugRules, 
      clinicDrugRates: detailGroupData?.clinicDrugRates,
    };

    let next = mergeDetailRatesFromServer(base, serverData, isHospitalScenario);

    // 병원 시나리오: 전체→개별 동기화
    if (isHospitalScenario) {
      next = syncHospitalAllPharmaDrugsToEachPharma(next, true);
    }

    // 선택된 제약사 ID 복원
    next.selectedPharmaId = isHospitalScenario ? 'all-pharma' : next.selectedPharmaId;

    // 스냅샷 저장 및 더티 리셋
    originalDetailDataSnapshotRef.current = buildDetailDataSnapshot(next);
    setIsDirty(false);
    setDetailData(next);
  }, [relationshipRows, selectedClinicId, isHospitalScenario, detailGroupData]);

  // 더티 상태 체크
  useEffect(() => {
    const originalSnapshot = originalDetailDataSnapshotRef.current;
    if (!originalSnapshot) {
      setIsDirty(false);
      return;
    }
    setIsDirty(buildDetailDataSnapshot(detailData) !== originalSnapshot);
  }, [detailData]);

  // ===== 데이터 업데이트 헬퍼 =====

  const updateDetailData = (updater: (prev: DetailDataUi) => DetailDataUi) => {
    setDetailData(updater);
  };

  /**
   * 수수료율 행 업데이트
   */
  const updateRows = (
    groupType: 'pharma' | 'drug',
    groupId: string,
    rowId: string,
    field: keyof RateRowUi,
    value: any
  ) => {
    updateDetailData((prev) => {
      if (groupType === 'pharma') {
        return {
          ...prev,
          pharmaGroups: prev.pharmaGroups.map((group) => {
            if (group.id !== groupId) return group;
            return {
              ...group,
              rows: (group.rows || []).map((row) =>
                row.id === rowId ? { ...row, [field]: value } : row
              ),
            };
          }),
        };
      }

      // 의약품 그룹 업데이트
      const selectedPharmaId = prev.selectedPharmaId;
      if (!selectedPharmaId) return prev;

      const selectedDrugGroups = prev.drugGroupsByPharmaId[selectedPharmaId] || [];
      const next = {
        ...prev,
        drugGroupsByPharmaId: {
          ...prev.drugGroupsByPharmaId,
          [selectedPharmaId]: selectedDrugGroups.map((group) => {
            if (group.id !== groupId) return group;
            return {
              ...group,
              rows: (group.rows || []).map((row) =>
                row.id === rowId ? { ...row, [field]: value } : row
              ),
            };
          }),
        },
      };

      // 병원 시나리오 동기화
      if (isHospitalScenario) {
        return selectedPharmaId === 'all-pharma'
          ? syncHospitalAllPharmaDrugsToEachPharma(next, true)
          : syncHospitalSelectedPharmaDrugsToAllPharma(next, selectedPharmaId, true);
      }
      
      return next;
    });
  };

  /**
   * 수수료율 행 추가
   */
  const addRow = (groupType: 'pharma' | 'drug', groupId: string) => {
    updateDetailData((prev) => {
      if (groupType === 'pharma') {
        return {
          ...prev,
          pharmaGroups: prev.pharmaGroups.map((group) =>
            group.id === groupId
              ? {
                  ...group,
                  rows: [...(group.rows || []), createNextSequentialRateRow(group.rows)],
                }
              : group
          ),
        };
      }

      const selectedPharmaId = prev.selectedPharmaId;
      if (!selectedPharmaId) return prev;

      const selectedDrugGroups = prev.drugGroupsByPharmaId[selectedPharmaId] || [];
      const next = {
        ...prev,
        drugGroupsByPharmaId: {
          ...prev.drugGroupsByPharmaId,
          [selectedPharmaId]: selectedDrugGroups.map((group) =>
            group.id === groupId
              ? {
                  ...group,
                  rows: [...(group.rows || []), createNextSequentialRateRow(group.rows)],
                }
              : group
          ),
        },
      };

      if (isHospitalScenario) {
        return selectedPharmaId === 'all-pharma'
          ? syncHospitalAllPharmaDrugsToEachPharma(next, true)
          : syncHospitalSelectedPharmaDrugsToAllPharma(next, selectedPharmaId, true);
      }
      
      return next;
    });
  };

  /**
   * 수수료율 행 삭제
   */
  const removeRow = (groupType: 'pharma' | 'drug', groupId: string, rowId: string) => {
    updateDetailData((prev) => {
      if (groupType === 'pharma') {
        return {
          ...prev,
          pharmaGroups: prev.pharmaGroups.map((group) =>
            group.id === groupId
              ? {
                  ...group,
                  rows: (group.rows || []).filter((row) => row.id !== rowId),
                  fixedEnabled: (group.rows || []).filter((row) => row.id !== rowId).length > 0,
                }
              : group
          ),
        };
      }

      const selectedPharmaId = prev.selectedPharmaId;
      if (!selectedPharmaId) return prev;

      const selectedDrugGroups = prev.drugGroupsByPharmaId[selectedPharmaId] || [];
      const next = {
        ...prev,
        drugGroupsByPharmaId: {
          ...prev.drugGroupsByPharmaId,
          [selectedPharmaId]: selectedDrugGroups
            .map((group) => {
              if (group.id !== groupId) return group;
              const nextRows = (group.rows || []).filter((row) => row.id !== rowId);
              
              // 의원이고 행이 없으면 그룹 자체 삭제
              if (!isHospitalScenario && nextRows.length === 0) {
                return null;
              }
              
              return {
                ...group,
                rows: nextRows,
                fixedEnabled: nextRows.length > 0 ? group.fixedEnabled : false,
              };
            })
            .filter(Boolean) as DrugGroupUi[],
        },
      };

      if (isHospitalScenario) {
        return selectedPharmaId === 'all-pharma'
          ? syncHospitalAllPharmaDrugsToEachPharma(next, true)
          : syncHospitalSelectedPharmaDrugsToAllPharma(next, selectedPharmaId, true);
      }
      
      return next;
    });
  };

  /**
   * 고정 수수료율 토글
   * 해제 시 기존 행이 있으면 확인 모달 (레거시 confirm)
   */
  const requestToggleFixed = (
    groupType: 'pharma' | 'drug',
    groupId: string,
    checked: boolean,
  ) => {
    if (!checked) {
      const group =
        groupType === 'pharma'
          ? detailData.pharmaGroups.find((g) => g.id === groupId)
          : (detailData.drugGroupsByPharmaId[detailData.selectedPharmaId || ''] || []).find(
              (g) => g.id === groupId,
            );
      const hasRows = Array.isArray(group?.rows) && group.rows.length > 0;
      if (hasRows) {
        setUncheckFixedTarget({ groupType, groupId });
        return;
      }
    }
    toggleFixed(groupType, groupId, checked);
  };

  const toggleFixed = (groupType: 'pharma' | 'drug', groupId: string, checked: boolean) => {
    updateDetailData((prev) => {
      if (groupType === 'pharma') {
        return {
          ...prev,
          pharmaGroups: prev.pharmaGroups.map((group) => {
            if (group.id !== groupId) return group;
            const hasExistingRows = Array.isArray(group.rows) && group.rows.length > 0;
            const nextRows = checked
              ? hasExistingRows
                ? group.rows.map((row) => ({ ...row, isApplied: true }))
                : createCurrentToNextMonthRateRow()
              : (group.rows || []).map((row) => ({ ...row, isApplied: false }));
            
            return {
              ...group,
              fixedEnabled: checked,
              rows: nextRows,
            };
          }),
        };
      }

      const selectedPharmaId = prev.selectedPharmaId;
      if (!selectedPharmaId) return prev;

      const selectedDrugGroups = prev.drugGroupsByPharmaId[selectedPharmaId] || [];
      const next = {
        ...prev,
        drugGroupsByPharmaId: {
          ...prev.drugGroupsByPharmaId,
          [selectedPharmaId]: selectedDrugGroups.map((group) => {
            if (group.id !== groupId) return group;
            const hasExistingRows = Array.isArray(group.rows) && group.rows.length > 0;
            const nextRows = checked
              ? hasExistingRows
                ? group.rows.map((row) => ({ ...row, isApplied: true }))
                : createCurrentToNextMonthRateRow()
              : (group.rows || []).map((row) => ({ ...row, isApplied: false }));
            
            return {
              ...group,
              fixedEnabled: checked,
              rows: nextRows,
            };
          }),
        },
      };

      if (isHospitalScenario) {
        return selectedPharmaId === 'all-pharma'
          ? syncHospitalAllPharmaDrugsToEachPharma(next, true)
          : syncHospitalSelectedPharmaDrugsToAllPharma(next, selectedPharmaId, true);
      }
      
      return next;
    });
  };

  /**
   * 제약사 선택
   */
  const selectPharma = (groupId: string) => {
    setDetailData((prev) => ({ ...prev, selectedPharmaId: groupId }));
  };

  /**
   * 의약품 추가 (의원 전용)
   */
  const handleAddDrugs = (drugs: Array<{ drugId: number | null; code: string; name: string }>) => {
    if (!detailData.selectedPharmaId || !selectedPharmaGroup) return;

    updateDetailData((prev) => {
      const selectedPharmaId = prev.selectedPharmaId!;
      const selectedDrugGroups = prev.drugGroupsByPharmaId[selectedPharmaId] || [];
      
      const newGroups = drugs.map((drug) => ({
        id: `${selectedPharmaId}-custom-${drug.code}-${Date.now()}-${Math.random()}`,
        drugId: drug.drugId,
        pharmaCompanyId: selectedPharmaGroup.pharmaCompanyId,
        name: drug.name,
        code: drug.code,
        fixedEnabled: true,
        isCustomAdded: true,
        rows: createCurrentToNextMonthRateRow(),
      }));

      return {
        ...prev,
        drugGroupsByPharmaId: {
          ...prev.drugGroupsByPharmaId,
          [selectedPharmaId]: [...selectedDrugGroups, ...newGroups],
        },
      };
    });
  };

  // ===== 저장 로직 =====

  const handleSubmitRates = async () => {
    if (!detailGroupId) return;

    try {
      setSaving(true);

      if (isHospitalScenario) {
        // 병원: 의약품 규칙만 저장
        const payload = buildHospitalDrugRulesPayload(detailGroupId, detailData.drugGroupsByPharmaId);
        const savedRules = detailGroupData?.hospitalDrugRules || [];
        const isChanged = !areRatesEqual(payload.rules, savedRules, normalizeDrugRate);

        if (isChanged) {
          await saveHospitalMutation.mutateAsync(payload);
        }
      } else {
        // 의원: 제약사 + 의약품 수수료율 저장
        const pharmaPayload = buildClinicPharmaRatesPayload(detailGroupId, detailData.pharmaGroups);
        const drugPayload = buildClinicDrugRatesPayload(
          detailGroupId,
          detailData.drugGroupsByPharmaId,
          detailData.pharmaGroups
        );

        const savedPharmaRates = clinicDetailGroup?.clinicPharmaRates || [];
        const savedDrugRates = clinicDetailGroup?.clinicDrugRates || [];

        const isPharmaChanged = !areRatesEqual(pharmaPayload.rates, savedPharmaRates, normalizePharmaRate);
        const isDrugChanged = !areRatesEqual(drugPayload.rates, savedDrugRates, normalizeDrugRate);

        if (isPharmaChanged) {
          await saveClinicPharmaMutation.mutateAsync(pharmaPayload);
        }

        if (isDrugChanged) {
          await saveClinicDrugMutation.mutateAsync(drugPayload);
        }
      }

      // 저장 후 데이터 재조회 및 더티 리셋
      await fetchDetailGroup(selectedClinicId!);
      toast.success('설정이 완료되었습니다.');
    } catch (error) {
      console.error('Failed to submit rates:', error);
      handleApiError(error, toast);
    } finally {
      setSaving(false);
    }
  };

  // ===== 이벤트 핸들러 =====

  const handleBackToList = () => {
    navigate({ to: ROUTE_PATHS.DETAIL_FEE_RATES as any });
  };

  const handleExit = () => {
    if (isDirty) {
      setExitOpen(true);
    } else {
      handleBackToList();
    }
  };

  const handleClinicChange = (clinicId: string) => {
    if (isDirty) {
      setClinicChangeBlockedOpen(true);
      return;
    }
    setSelectedClinicId(Number(clinicId));
  };

  // ===== 렌더링 헬퍼 =====

  /**
   * 수수료율 행들 렌더링
   */
  const renderRateRows = (groupType: 'pharma' | 'drug', group: PharmaGroupUi | DrugGroupUi) => {
    if (!group.rows || group.rows.length === 0) return null;

    const isFixedRateDisabled =
      readOnly ||
      (!isHospitalScenario && groupType === 'pharma' && !group.fixedEnabled) ||
      (isHospitalScenario && groupType === 'drug' && !group.fixedEnabled);

    return (
      <Stack className="space-y-3">
        {group.rows.map((row) => (
          <Card 
            key={row.id} 
            className={cn(
              'rounded-12 border-ruda-border-light p-4',
              isFixedRateDisabled && 'opacity-65'
            )}
          >
            <div className="flex flex-wrap items-center gap-4 justify-between">
              {/* 기간 설정 */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-ruda-text-secondary">기간</span>
                <MonthPicker
                  value={row.startMonth}
                  disabled={isFixedRateDisabled}
                  onChange={(value) => updateRows(groupType, group.id, row.id, 'startMonth', value)}
                  className="w-32"
                />
                <span className="text-sm">~</span>
                <MonthPicker
                  value={row.endMonth}
                  disabled={isFixedRateDisabled}
                  onChange={(value) => updateRows(groupType, group.id, row.id, 'endMonth', value)}
                  className="w-32"
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isFixedRateDisabled}
                  onClick={() => addRow(groupType, group.id)}
                  className="h-8 w-8 p-0"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* 수수료율 설정 */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-ruda-text-secondary">수수료율</span>
                <div className="relative">
                  {/* type="number"는 '-' 단독 입력을 빈 값으로 돌려줘 마이너스 입력이 막히므로 text + draft로 받는다 */}
                  <Input
                    type="text"
                    inputMode="decimal"
                    aria-label="수수료율"
                    value={
                      focusedRateRowId === row.id
                        ? focusedRateDraft
                        : formatRateInput(row.rate)
                    }
                    disabled={isFixedRateDisabled}
                    onFocus={() => {
                      setFocusedRateRowId(row.id);
                      setFocusedRateDraft(String(row.rate ?? ''));
                    }}
                    onBlur={() => {
                      updateRows(
                        groupType,
                        group.id,
                        row.id,
                        'rate',
                        roundSignedRate(Number(focusedRateDraft) || 0),
                      );
                      setFocusedRateRowId(null);
                      setFocusedRateDraft('');
                    }}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (!/^-?\d*\.?\d*$/.test(raw)) return;
                      setFocusedRateDraft(raw);
                      const next = Number(raw);
                      if (raw === '' || raw === '-' || Number.isNaN(next)) return;
                      updateRows(groupType, group.id, row.id, 'rate', next);
                    }}
                    className="w-24 pr-8"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-ruda-text-secondary">%</span>
                </div>
                
                {/* 구간 추가 적용 체크박스 (구간 수수료인 경우만) */}
                {isIntervalFee && (
                  <label className="flex items-center gap-2">
                    <Checkbox
                      checked={row.rangeApplied}
                      disabled={isFixedRateDisabled}
                      onCheckedChange={(checked) => 
                        updateRows(groupType, group.id, row.id, 'rangeApplied', Boolean(checked))
                      }
                    />
                    <span className="text-sm">구간 추가 적용</span>
                  </label>
                )}

                <Button
                  size="sm"
                  variant="outline"
                  disabled={isFixedRateDisabled}
                  onClick={() =>
                    setDeleteRowTarget({
                      groupType,
                      groupId: group.id,
                      rowId: row.id,
                    })
                  }
                  className="h-8 w-8 p-0 text-ruda-error hover:text-ruda-error"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </Stack>
    );
  };

  /**
   * 왼쪽 제약사 패널
   * 병원급은 이름 목록만 보여 간격을 좁게, 의원급은 고정 수수료율 토글·행이 있어 여유 있게 둔다.
   */
  const renderLeftPanel = () => (
    <Stack gap={isHospitalScenario ? 4 : 12}>
      {visiblePharmaGroups.map((group) => (
        <Card
          key={group.id}
          className={cn(
            'cursor-pointer rounded-12 border-ruda-border-light transition-colors hover:bg-ruda-bg-tertiary',
            isHospitalScenario ? 'px-3 py-2 shadow-none' : 'p-4',
            detailData.selectedPharmaId === group.id &&
              'border-ruda-primary bg-ruda-bg-tertiary',
          )}
          onClick={() => selectPharma(group.id)}
        >
          {isHospitalScenario ? (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-ruda-text-primary">
                {group.name}
                {group.id !== 'all-pharma' && (() => {
                  const configuredCount = (detailData.drugGroupsByPharmaId?.[group.id] || [])
                    .filter((drug) => drug.fixedEnabled && drug.rows.length > 0).length;
                  return configuredCount > 0 ? ` (${configuredCount})` : '';
                })()}
              </span>
            </div>
          ) : (
            <Stack gap={12}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-ruda-text-primary">
                  {group.name}
                  {(() => {
                    const drugCount = (detailData.drugGroupsByPharmaId?.[group.id] || []).length;
                    return drugCount > 0 ? ` (${drugCount})` : '';
                  })()}
                </span>
                <label className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={group.fixedEnabled}
                    disabled={readOnly}
                    onCheckedChange={(checked) =>
                      requestToggleFixed('pharma', group.id, Boolean(checked))
                    }
                  />
                  <span className="text-sm">제약사 고정 수수료율</span>
                </label>
              </div>
              
              {group.rows && group.rows.length > 0 && (
                <div className="mt-1">
                  {renderRateRows('pharma', group)}
                </div>
              )}
            </Stack>
          )}
        </Card>
      ))}
    </Stack>
  );

  /**
   * 오른쪽 의약품 패널
   */
  const renderRightPanel = () => (
    <Stack className="space-y-3">
      {!isHospitalScenario && !detailData.selectedPharmaId ? (
        <div className="py-12 text-center">
          <span className="text-ruda-text-secondary">제약사를 선택해 주세요</span>
        </div>
      ) : sortedCurrentDrugGroups.length === 0 ? (
        <div className="py-12 text-center">
          <span className="text-ruda-text-secondary">설정된 의약품이 없습니다</span>
        </div>
      ) : (
        sortedCurrentDrugGroups.map((group) => (
          <Card key={group.id} className="rounded-12 border-ruda-border-light p-4">
            <Stack className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-ruda-text-primary">{group.name}</div>
                  <div className="text-sm text-ruda-text-secondary">{group.code}</div>
                </div>
                {isHospitalScenario && (
                  <label className="flex items-center gap-2">
                    <Checkbox
                      checked={group.fixedEnabled}
                      disabled={readOnly}
                      onCheckedChange={(checked) =>
                        requestToggleFixed('drug', group.id, Boolean(checked))
                      }
                    />
                    <span className="text-sm">의약품 고정 수수료율</span>
                  </label>
                )}
              </div>

              {group.rows && group.rows.length > 0 && (
                <div className="mt-3">
                  {renderRateRows('drug', group)}
                </div>
              )}
            </Stack>
          </Card>
        ))
      )}
    </Stack>
  );

  if (clinicsLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <PageHeader
        title="상세 수수료율 설정하기"
        actions={
          <Button variant="outline" onClick={handleBackToList}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            나가기
          </Button>
        }
      />

      {/* 기본 정보 */}
      <Card className="rounded-12 border-ruda-border-light">
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="거래 병의원" className="flex-1">
              <Select
                value={selectedClinicId?.toString() || ''}
                onValueChange={handleClinicChange}
                disabled={!clinics || clinics.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="거래 병의원을 선택하세요" />
                </SelectTrigger>
                <SelectContent>
                  {clinics?.map((clinic) => (
                    <SelectItem key={clinic.clinicId} value={clinic.clinicId.toString()}>
                      <div className="flex items-center gap-2">
                        <span>
                          [{search.basicFeeType === 'RANGE' || search.basicFeeType === '구간' ? '구간' : '고정'} - {clinic.clinicType === 'HOSPITAL' ? '병원급' : '의원급'}]{' '}
                          {clinic.clinicName}
                        </span>
                        {clinic.detailFeeConfigured && (
                          <Badge variant="info" className="text-xs">설정중</Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            
            <Field label="사용자 이메일" className="flex-1">
              <Input value={email || userId || ''} disabled />
            </Field>
            
            <Field label="업체명" className="flex-1">
              <Input value={companyName || ''} disabled />
            </Field>
          </div>
        </CardContent>
      </Card>

      {/* 메인 컨텐츠 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 왼쪽: 제약사 목록 */}
        <Card className="rounded-12 border-ruda-border-light">
          <CardHeader>
            <CardTitle>{isHospitalScenario ? '제약사' : '승인 제약사'}</CardTitle>
          </CardHeader>
          <CardContent className="max-h-[560px] overflow-y-auto">
            {renderLeftPanel()}
          </CardContent>
        </Card>

        {/* 오른쪽: 의약품 수수료율 */}
        <Card className="rounded-12 border-ruda-border-light">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                {isHospitalScenario ? '승인 의약품 수수료율 설정' : '의약품 수수료율 설정'}
              </CardTitle>
              {!isHospitalScenario && (
                <MenuWriteGate>
                  <Button
                    size="sm"
                    onClick={() => setDrugAddModalOpen(true)}
                    disabled={!detailData.selectedPharmaId}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    추가
                  </Button>
                </MenuWriteGate>
              )}
            </div>
          </CardHeader>
          <CardContent className="max-h-[560px] overflow-y-auto">
            {renderRightPanel()}
          </CardContent>
        </Card>
      </div>

      {/* 하단 액션 */}
      <div className="text-center space-y-4">
        {hasInvalidPeriod && (
          <Alert className="text-left">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              시작기간은 종료기간보다 이후일 수 없습니다. ({invalidPeriodTarget})
            </AlertDescription>
          </Alert>
        )}
        
        <ToolbarRow className="justify-center">
          <Button
            variant="outline"
            onClick={handleExit}
            className="min-w-40"
          >
            나가기
          </Button>
          <MenuWriteGate>
            <Button
              onClick={handleSubmitRates}
              disabled={!isDirty || saving || hasInvalidPeriod}
              loading={saving}
              className="ruda-button min-w-40"
            >
              <Save className="mr-2 h-4 w-4" />
              설정 완료
            </Button>
          </MenuWriteGate>
        </ToolbarRow>
      </div>

      {/* 나가기 확인 모달 */}
      <AlertDialog open={exitOpen} onOpenChange={setExitOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>나가기</AlertDialogTitle>
            <AlertDialogDescription>
              변경된 정보가 있습니다.
              <br />
              나가시면 변경 내역은 삭제됩니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction onClick={handleBackToList}>나가기</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 클리닉 변경 차단 모달 */}
      <AlertDialog open={clinicChangeBlockedOpen} onOpenChange={setClinicChangeBlockedOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>저장 필요</AlertDialogTitle>
            <AlertDialogDescription>
              변경된 설정이 있습니다. 병의원을 변경하려면 먼저 설정을 저장해 주세요.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>확인</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 행 삭제 확인 — 레거시 Modal.confirm */}
      <AlertDialog
        open={Boolean(deleteRowTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteRowTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>기간 삭제</AlertDialogTitle>
            <AlertDialogDescription>
              해당 기간에 설정된 수수료율이
              <br />
              삭제됩니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!deleteRowTarget) return;
                removeRow(
                  deleteRowTarget.groupType,
                  deleteRowTarget.groupId,
                  deleteRowTarget.rowId,
                );
                setDeleteRowTarget(null);
              }}
            >
              삭제
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 고정 수수료율 해제 확인 — 레거시 Modal.confirm */}
      <AlertDialog
        open={Boolean(uncheckFixedTarget)}
        onOpenChange={(open) => {
          if (!open) setUncheckFixedTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>고정 수수료율 해제</AlertDialogTitle>
            <AlertDialogDescription>
              설정된 정보가 있습니다.
              <br />
              해제 시 설정한 수수료율은 적용되지 않습니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!uncheckFixedTarget) return;
                toggleFixed(
                  uncheckFixedTarget.groupType,
                  uncheckFixedTarget.groupId,
                  false,
                );
                setUncheckFixedTarget(null);
              }}
            >
              해제
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 의약품 추가 모달 (의원 전용) */}
      {!isHospitalScenario && selectedPharmaGroup && (
        <DetailFeeDrugAddModal
          open={drugAddModalOpen}
          pharmaCompanyName={selectedPharmaGroup.name}
          existingDrugCodes={sortedCurrentDrugGroups.map(d => d.code)}
          onClose={() => setDrugAddModalOpen(false)}
          onConfirm={(drugs) => {
            const mappedDrugs = drugs.map(d => ({
              drugId: d.drugId,
              code: d.drugCode,
              name: d.drugName,
            }));
            handleAddDrugs(mappedDrugs);
            setDrugAddModalOpen(false);
          }}
        />
      )}
    </div>
  );
};